import React, { useState, useEffect, useCallback } from 'react';
import { parseApiUtc } from '@/utils/apiDate';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Clock, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Plus, Ban, DollarSign, TrendingDown, Users,
} from 'lucide-react';
import PageShell from '../../../components/leaves/PageShell';
import Button from '../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '../../../components/ui/Dialog';
import { EncashmentRequestView, EncashmentEligibilityView, BatchEncashmentPreviewView, BatchEncashmentResultView, LeaveType } from '../../../models/attendance';
import * as LeavesApi from '../../../api/Attendance/leaves';
import { getFullUrl } from '../../../utils/url';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { appDate } from '../../../utils/appDateFormat';

interface EncashmentProps {
  userId: string;
  company: { id: number };
  role: string;
}

type TabId = 'pending' | 'my' | 'batch';

const statusConfig: Record<string, { bgClass: string; icon: React.ElementType }> = {
  Pending: { bgClass: 'bg-amber-100 text-amber-800', icon: Clock },
  Approved: { bgClass: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  Rejected: { bgClass: 'bg-red-100 text-red-800', icon: XCircle },
  Cancelled: { bgClass: 'bg-muted text-muted-foreground', icon: Ban },
};

// createdOn / approvedOn are UTC instants — show the viewer's local day.
function formatDate(dateStr: string): string {
  return appDate(parseApiUtc(dateStr));
}

const Encashment: React.FC<EncashmentProps> = ({ role }) => {
  const { t } = useTranslation(['leaves', 'common']);
  const isManagerOrHr = ['Manager', 'Hr', 'Chief'].includes(role);

  const [activeTab, setActiveTab] = useState<TabId>(isManagerOrHr ? 'pending' : 'my');
  const [pendingRequests, setPendingRequests] = useState<EncashmentRequestView[]>([]);
  const [myRequests, setMyRequests] = useState<EncashmentRequestView[]>([]);
  const [eligibility, setEligibility] = useState<EncashmentEligibilityView[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [requestModal, setRequestModal] = useState(false);
  const [approvalModal, setApprovalModal] = useState<{ request: EncashmentRequestView; action: 'approve' | 'reject' } | null>(null);
  const [cancelModal, setCancelModal] = useState<EncashmentRequestView | null>(null);
  const [requestForm, setRequestForm] = useState({
    leaveTypeId: 0,
    daysRequested: 0,
    notes: '',
  });
  const [approvalComments, setApprovalComments] = useState('');

  // Batch encashment state (HR only)
  const isHr = role === 'Hr' || role === 'Chief';
  const [batchLeaveTypes, setBatchLeaveTypes] = useState<LeaveType[]>([]);
  const [batchLeaveTypeId, setBatchLeaveTypeId] = useState(0);
  const [batchPreview, setBatchPreview] = useState<BatchEncashmentPreviewView[]>([]);
  const [batchSelected, setBatchSelected] = useState<Record<string, number>>({});
  const [batchResult, setBatchResult] = useState<BatchEncashmentResultView | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, myRes, eligRes] = await Promise.all([
        isManagerOrHr ? LeavesApi.getPendingEncashments() : Promise.resolve({ errors: [], payload: [] as EncashmentRequestView[] }),
        LeavesApi.getMyEncashments(),
        LeavesApi.getEncashmentEligibility(),
      ]);
      if (pendingRes.errors.length === 0) setPendingRequests(pendingRes.payload);
      if (myRes.errors.length === 0) setMyRequests(myRes.payload);
      if (eligRes.errors.length === 0) setEligibility(eligRes.payload);

      // Load leave types for batch mode
      if (isHr) {
        const typesRes = await LeavesApi.getCompanyLeaveTypes();
        if (typesRes.errors.length === 0) setBatchLeaveTypes(typesRes.payload);
      }
    } catch {
      setToast({ message: t('encashmentLoadError'), type: 'error' });
    }
    setLoading(false);
  }, [isManagerOrHr, isHr, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const selectedEligibility = eligibility.find(e => e.leaveTypeId === requestForm.leaveTypeId);

  const handleRequest = async () => {
    setActionLoading(-1);
    try {
      const result = await LeavesApi.requestEncashment(requestForm);
      if (result.errors.length === 0) {
        setToast({ message: t('encashmentRequested'), type: 'success' });
        setRequestModal(false);
        setRequestForm({ leaveTypeId: 0, daysRequested: 0, notes: '' });
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('encashmentRequestError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('encashmentRequestError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const handleApproval = async () => {
    if (!approvalModal) return;
    setActionLoading(approvalModal.request.id);
    try {
      const fn = approvalModal.action === 'approve' ? LeavesApi.approveEncashment : LeavesApi.rejectEncashment;
      const result = await fn(approvalModal.request.id, { comments: approvalComments || undefined });
      if (result.errors.length === 0) {
        setToast({ message: t(approvalModal.action === 'approve' ? 'encashmentApproved' : 'encashmentRejected'), type: 'success' });
        setApprovalModal(null);
        setApprovalComments('');
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('encashmentActionError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('encashmentActionError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setActionLoading(cancelModal.id);
    try {
      const result = await LeavesApi.cancelEncashment(cancelModal.id);
      if (result.errors.length === 0) {
        setToast({ message: t('encashmentCancelled'), type: 'success' });
        setCancelModal(null);
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('encashmentCancelError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('encashmentCancelError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const handleBatchPreview = async (leaveTypeId: number) => {
    setBatchLeaveTypeId(leaveTypeId);
    setBatchLoading(true);
    setBatchResult(null);
    try {
      const result = await LeavesApi.getBatchEncashmentPreview(leaveTypeId);
      if (result.errors.length === 0) {
        setBatchPreview(result.payload);
        const selected: Record<string, number> = {};
        result.payload.forEach(p => { selected[p.userId] = p.encashableDays; });
        setBatchSelected(selected);
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('batchPreviewError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('batchPreviewError')), type: 'error' });
    }
    setBatchLoading(false);
  };

  const handleBatchProcess = async () => {
    setBatchProcessing(true);
    try {
      const employees = Object.entries(batchSelected)
        .filter(([, days]) => days > 0)
        .map(([userId, days]) => ({ userId, daysToEncash: days }));

      const result = await LeavesApi.processBatchEncashment({
        leaveTypeId: batchLeaveTypeId,
        employees,
      });
      if (result.errors.length === 0) {
        setBatchResult(result.payload);
        setToast({ message: t('batchProcessed', { success: result.payload.successCount, failed: result.payload.failedCount }), type: result.payload.failedCount > 0 ? 'error' : 'success' });
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('batchProcessError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('batchProcessError')), type: 'error' });
    }
    setBatchProcessing(false);
  };

  const tabs = [
    ...(isManagerOrHr ? [{ id: 'pending' as TabId, label: t('Pending Approvals'), count: pendingRequests.length }] : []),
    { id: 'my' as TabId, label: t('My Encashments'), count: myRequests.length },
    ...(isHr ? [{ id: 'batch' as TabId, label: t('Batch Processing'), count: 0 }] : []),
  ];

  const renderStatusBadge = (status: string) => {
    const config = statusConfig[status] || statusConfig.Pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgClass}`}>
        <Icon className="w-3 h-3" />
        {t(status)}
      </span>
    );
  };

  const renderEligibilityCards = () => {
    if (eligibility.length === 0) return null;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {eligibility.map(e => (
          <div key={e.leaveTypeId} className="bg-card rounded-xl border border-border p-4" data-testid={`eligibility-${e.leaveTypeId}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-foreground">{e.leaveTypeName}</h4>
              <DollarSign className="w-4 h-4 text-green-500" />
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{t('Balance')}</span>
                <span className="font-medium text-foreground">{e.currentBalance.toFixed(1)} {t('days')}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{t('Floor')}</span>
                <span className="font-medium text-foreground">{e.balanceFloor.toFixed(1)} {t('days')}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{t('Encashable')}</span>
                <span className="font-bold text-green-600">{e.maxEncashable.toFixed(1)} {t('days')}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderRequestCard = (req: EncashmentRequestView, showActions: boolean) => (
    <div key={req.id} className="bg-card rounded-xl border border-border p-5 hover:shadow-md transition-shadow" data-testid={`encashment-card-${req.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            {getFullUrl(req.userImageUrl) ? (
              <img src={getFullUrl(req.userImageUrl)!} alt={req.userName} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <DollarSign className="w-5 h-5 text-green-600" />
            )}
          </div>
          <div>
            <p className="font-semibold text-foreground">{req.userName}</p>
            <p className="text-xs text-muted-foreground">{formatDate(req.createdOn)}</p>
          </div>
        </div>
        {renderStatusBadge(req.status)}
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">{t('Leave Type')}</p>
          <p className="text-sm font-medium text-foreground">{req.leaveTypeName}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('Days Requested')}</p>
          <p className="text-sm font-medium text-foreground">{req.daysRequested.toFixed(1)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('Balance Before')}</p>
          <p className="text-sm font-medium text-foreground">{req.balanceBefore.toFixed(1)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('Balance After')}</p>
          <p className="text-sm font-medium text-foreground">
            <span className="inline-flex items-center gap-1">
              {req.balanceAfter.toFixed(1)}
              <TrendingDown className="w-3 h-3 text-red-500" />
            </span>
          </p>
        </div>
      </div>

      {req.notes && (
        <div className="mt-3">
          <p className="text-xs text-muted-foreground">{t('Notes')}</p>
          <p className="text-sm text-foreground mt-0.5">{req.notes}</p>
        </div>
      )}

      {req.rejectionReason && (
        <div className="mt-3">
          <p className="text-xs text-muted-foreground">{t('Rejection Reason')}</p>
          <p className="text-sm text-red-600 mt-0.5">{req.rejectionReason}</p>
        </div>
      )}

      {req.approvedByName && (
        <div className="mt-3">
          <p className="text-xs text-muted-foreground">{t('Approved By')}</p>
          <p className="text-sm text-foreground mt-0.5">{req.approvedByName} {req.approvedOn ? `(${formatDate(req.approvedOn)})` : ''}</p>
        </div>
      )}

      {showActions && req.status === 'Pending' && (
        <div className="mt-4 flex gap-2">
          {activeTab === 'pending' && isManagerOrHr ? (
            <>
              <Button
                onClick={() => { setApprovalModal({ request: req, action: 'approve' }); setApprovalComments(''); }}
                disabled={actionLoading === req.id}
                variant="success"
                className="flex-1"
                data-testid={`approve-encashment-${req.id}`}
                iconName="CheckCircle2"
              >
                {t('Approve')}
              </Button>
              <Button
                onClick={() => { setApprovalModal({ request: req, action: 'reject' }); setApprovalComments(''); }}
                disabled={actionLoading === req.id}
                variant="destructive"
                className="flex-1"
                data-testid={`reject-encashment-${req.id}`}
                iconName="XCircle"
              >
                {t('Reject')}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setCancelModal(req)}
              disabled={actionLoading === req.id}
              variant="secondary"
              data-testid={`cancel-encashment-${req.id}`}
              iconName="Ban"
            >
              {t('Cancel')}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  const currentList = activeTab === 'pending' ? pendingRequests : myRequests;

  return (
    <PageShell
      title={t('Leave Encashment')}
      description={t('encashmentDescription')}
      icon={DollarSign}
      accentColor="emerald"
      maxWidth="5xl"
      stats={[
        { label: t('Pending Approvals'), value: pendingRequests.length, color: 'text-amber-600' },
        { label: t('My Encashments'), value: myRequests.length },
        { label: t('Eligible Types'), value: eligibility.length, color: 'text-green-600' },
      ]}
      headerActions={eligibility.length > 0 ? (
        <Button
          onClick={() => {
            setRequestForm({ leaveTypeId: eligibility[0].leaveTypeId, daysRequested: 0, notes: '' });
            setRequestModal(true);
          }}
          variant="outline"
          data-testid="request-encashment-btn"
          iconName="Plus"
        >
          {t('Request Encashment')}
        </Button>
      ) : undefined}
      className="min-h-screen bg-background"
    >
      {/* Eligibility cards */}
      <div className="pt-6">
        {renderEligibilityCards()}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-xl mb-6" data-testid="encashment-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                activeTab === tab.id ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
        </div>
      ) : activeTab === 'batch' ? (
        <div data-testid="batch-encashment-section">
          {/* Leave type selector */}
          <div className="bg-card rounded-xl border border-border p-5 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-5 h-5 text-green-600" />
              <h3 className="text-base font-semibold text-foreground">{t('Batch Year-End Encashment')}</h3>
            </div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground mb-1">{t('Leave Type')}</label>
                <select
                  value={batchLeaveTypeId}
                  onChange={(e) => setBatchLeaveTypeId(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="batch-leave-type"
                >
                  <option value={0}>{t('Select leave type')}</option>
                  {batchLeaveTypes.map(lt => (
                    <option key={lt.id} value={lt.id}>{lt.name}</option>
                  ))}
                </select>
              </div>
              <Button
                onClick={() => handleBatchPreview(batchLeaveTypeId)}
                disabled={!batchLeaveTypeId || batchLoading}
                variant="success"
                loading={batchLoading}
                data-testid="batch-preview-btn"
              >
                {t('Load Preview')}
              </Button>
            </div>
          </div>

          {/* Batch result summary */}
          {batchResult && (
            <div className={`rounded-xl border p-4 mb-6 ${batchResult.failedCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <h4 className="font-semibold text-foreground mb-2">{t('Batch Processing Results')}</h4>
              <div className="flex gap-6 text-sm">
                <span>{t('Total')}: {batchResult.totalProcessed}</span>
                <span className="text-green-600">{t('Success')}: {batchResult.successCount}</span>
                {batchResult.failedCount > 0 && (
                  <span className="text-red-600">{t('Failed')}: {batchResult.failedCount}</span>
                )}
              </div>
              {batchResult.results.filter(r => !r.success).length > 0 && (
                <div className="mt-3 space-y-1">
                  {batchResult.results.filter(r => !r.success).map((r, i) => (
                    <p key={i} className="text-xs text-red-600">{r.userName}: {r.errorMessage}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Preview table */}
          {batchPreview.length > 0 && !batchResult && (
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">
                  {t('eligibleEmployees', { count: batchPreview.length })}
                </p>
                <Button
                  onClick={handleBatchProcess}
                  disabled={batchProcessing || Object.values(batchSelected).every(d => d <= 0)}
                  variant="success"
                  loading={batchProcessing}
                  data-testid="batch-process-btn"
                >
                  {t('Process Batch')}
                </Button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-start px-4 py-3 font-medium text-muted-foreground">{t('Employee')}</th>
                    <th className="text-end px-4 py-3 font-medium text-muted-foreground">{t('Balance')}</th>
                    <th className="text-end px-4 py-3 font-medium text-muted-foreground">{t('Floor')}</th>
                    <th className="text-end px-4 py-3 font-medium text-muted-foreground">{t('Encashable')}</th>
                    <th className="text-end px-4 py-3 font-medium text-muted-foreground">{t('Days to Encash')}</th>
                  </tr>
                </thead>
                <tbody>
                  {batchPreview.map(p => (
                    <tr key={p.userId} className="border-t border-border hover:bg-muted">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-xs font-semibold text-green-700">
                            {p.userName.charAt(0)}
                          </div>
                          <span className="font-medium text-foreground">{p.userName}</span>
                        </div>
                      </td>
                      <td className="text-end px-4 py-3 text-foreground">{p.currentBalance.toFixed(1)}</td>
                      <td className="text-end px-4 py-3 text-muted-foreground">{p.balanceFloor.toFixed(1)}</td>
                      <td className="text-end px-4 py-3 font-medium text-green-600">{p.encashableDays.toFixed(1)}</td>
                      <td className="text-end px-4 py-3">
                        <input
                          type="number"
                          min={0}
                          max={p.encashableDays}
                          step={0.5}
                          value={batchSelected[p.userId] ?? 0}
                          onChange={(e) => setBatchSelected(prev => ({ ...prev, [p.userId]: Math.min(parseFloat(e.target.value) || 0, p.encashableDays) }))}
                          className="w-20 px-2 py-1 border border-border rounded text-sm text-end focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                          data-testid={`batch-days-${p.userId}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {batchPreview.length === 0 && !batchLoading && batchLeaveTypeId > 0 && (
            <div className="text-center py-12">
              <Users className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">{t('noEligibleEmployees')}</p>
            </div>
          )}
        </div>
      ) : currentList.length === 0 ? (
        <div className="text-center py-20" data-testid="encashment-empty">
          <DollarSign className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">{t('noEncashments')}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {activeTab === 'pending' ? t('noPendingEncashments') : t('noEncashmentRequests')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {currentList.map(req => renderRequestCard(req, true))}
        </div>
      )}

      {/* Request Modal */}
      <Dialog open={requestModal} onOpenChange={setRequestModal}>
        <DialogContent data-testid="request-encashment-modal">
          <DialogHeader>
            <DialogTitle>{t('Request Encashment')}</DialogTitle>
            <DialogDescription>{t('encashmentRequestDescription')}</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('Leave Type')}</label>
              <select
                value={requestForm.leaveTypeId}
                onChange={(e) => setRequestForm({ ...requestForm, leaveTypeId: parseInt(e.target.value), daysRequested: 0 })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="encashment-leave-type"
              >
                {eligibility.map(e => (
                  <option key={e.leaveTypeId} value={e.leaveTypeId}>
                    {e.leaveTypeName} ({t('Max')}: {e.maxEncashable.toFixed(1)} {t('days')})
                  </option>
                ))}
              </select>
            </div>
            {selectedEligibility && (
              <div className="bg-muted rounded-lg p-3">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{t('Current Balance')}</span>
                  <span className="font-medium">{selectedEligibility.currentBalance.toFixed(1)} {t('days')}</span>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-muted-foreground">{t('Balance Floor')}</span>
                  <span className="font-medium">{selectedEligibility.balanceFloor.toFixed(1)} {t('days')}</span>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-muted-foreground">{t('Max Encashable')}</span>
                  <span className="font-bold text-green-600">{selectedEligibility.maxEncashable.toFixed(1)} {t('days')}</span>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('Days to Encash')}</label>
              <input
                type="number"
                min={0.5}
                step={0.5}
                max={selectedEligibility?.maxEncashable || 0}
                value={requestForm.daysRequested || ''}
                onChange={(e) => setRequestForm({ ...requestForm, daysRequested: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="encashment-days"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('Notes')}</label>
              <textarea
                value={requestForm.notes}
                onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                placeholder={t('encashmentNotesPlaceholder')}
                data-testid="encashment-notes"
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              onClick={() => setRequestModal(false)}
              variant="outline"
              data-testid="cancel-request-btn"
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleRequest}
              disabled={actionLoading === -1 || requestForm.daysRequested <= 0 || !requestForm.leaveTypeId}
              loading={actionLoading === -1}
              data-testid="submit-encashment-btn"
            >
              {t('Submit Request')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Modal */}
      <Dialog open={!!approvalModal} onOpenChange={(o) => { if (!o) setApprovalModal(null); }}>
        <DialogContent data-testid="approval-modal">
          <DialogHeader>
            <DialogTitle>
              {approvalModal?.action === 'approve' ? t('Approve Encashment') : t('Reject Encashment')}
            </DialogTitle>
            <DialogDescription>
              {approvalModal?.request.userName} - {approvalModal?.request.daysRequested.toFixed(1)} {t('days')} ({approvalModal?.request.leaveTypeName})
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <label className="block text-sm font-medium text-foreground mb-1">{t('Comments')}</label>
            <textarea
              value={approvalComments}
              onChange={(e) => setApprovalComments(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
              placeholder={t('encashmentCommentsPlaceholder')}
              data-testid="approval-comments"
            />
          </DialogBody>
          <DialogFooter>
            <Button
              onClick={() => setApprovalModal(null)}
              variant="outline"
              data-testid="leave-encashment-approval-cancel"
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleApproval}
              disabled={actionLoading === approvalModal?.request.id}
              variant={approvalModal?.action === 'approve' ? 'success' : 'destructive'}
              loading={actionLoading === approvalModal?.request.id}
              data-testid="confirm-approval-btn"
            >
              {approvalModal?.action === 'approve' ? t('Approve') : t('Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={!!cancelModal} onOpenChange={(o) => { if (!o) setCancelModal(null); }}>
        <DialogContent size="sm" data-testid="cancel-modal">
          <DialogBody className="text-center pt-6">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-foreground">{t('Cancel Encashment')}</h3>
            <p className="text-sm text-muted-foreground mt-2">
              {cancelModal && t('confirmCancelEncashment', { days: cancelModal.daysRequested.toFixed(1), type: cancelModal.leaveTypeName })}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              onClick={() => setCancelModal(null)}
              variant="outline"
              className="flex-1"
              data-testid="leave-encashment-cancel-dismiss"
            >
              {t('Keep')}
            </Button>
            <Button
              onClick={handleCancel}
              disabled={cancelModal ? actionLoading === cancelModal.id : false}
              variant="destructive"
              className="flex-1"
              loading={cancelModal ? actionLoading === cancelModal.id : false}
              data-testid="confirm-cancel-btn"
            >
              {t('Cancel Request')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 end-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}
    </PageShell>
  );
};

const mapStateToProps = (state: { user: { userId: string; company: { id: number }; role: string } }) => ({
  userId: state.user.userId,
  company: state.user.company,
  role: state.user.role,
});

export default connect(mapStateToProps)(Encashment);
